import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HubspotStorageService } from '../services/hubspot-storage-service';

import { HubspotTokens } from '../models/hubspot-tokens';
import { environment } from '../../../environments/environment';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthContext } from '../models/auth-context';

@Injectable({
  providedIn: 'root',
})
export class HubspotAuthService {
  private _isAuthenticated = signal<boolean>(false);
  public isAuthenticated = this._isAuthenticated.asReadonly();

  private http = inject(HttpClient);
  private hubspotStorageService = inject(HubspotStorageService);

  // Cache for in-flight refresh token request to prevent multiple concurrent refreshes
  private refreshTokenInFlight$: Observable<HubspotTokens> | null = null;

  tokenData = signal<HubspotTokens | null>(null);
  tokenData$ = toObservable(this.tokenData);
  authHeaders$ = this.tokenData$.pipe(
    switchMap((tokenData) => {
      if (!tokenData) {
        return of(null);
      }

      if (this.isTokenExpired(tokenData)) {
        return this.refreshAccessToken(tokenData.refresh_token).pipe(
          map((tokenData) => tokenData.bearer),
        );
      }

      return of(tokenData.bearer);
    }),
    map((jwt) => {
      if (!jwt) {
        return null;
      }

      return {
        key: 'Authorization',
        value: `Bearer ${jwt}`,
      };
    }),
  );

  constructor() {
    this.initialize();
  }

  private initialize() {
    const tokenData = this.hubspotStorageService.getTokens();
    this.tokenData.set(tokenData);
    this._isAuthenticated.set(!!tokenData);
  }

  initiateAuth(context: AuthContext) {
    const tokens = this.hubspotStorageService.getTokens();

    if (tokens && !this.isTokenExpired(tokens)) {
      return null;
    }

    return this.getOAuthRedirectUrl(context);
  }

  private getOAuthRedirectUrl(context: AuthContext) {
    const state = btoa(JSON.stringify(context));

    const params = new URLSearchParams({
      client_id: environment.hubspot.clientId,
      redirect_uri: environment.hubspot.redirectUri,
      scope: environment.hubspot.scope.join(' '),
      state,
    });

    return `${environment.hubspot.endpoint}/oauth/authorize?${params.toString()}`;
  }

  handleOAuthCallback(code: string) {
    return this.exchangeCodeForTokens(code).pipe(
      tap((tokens) => {
        this.hubspotStorageService.setTokens(tokens);
        this.tokenData.set(tokens);
        this._isAuthenticated.set(true);
      }),
    );
  }

  private exchangeCodeForTokens(code: string) {
    return this.http
      .post<HubspotTokens>(
        `${environment.endpoint}/v1/hubspot/exchange_token`,
        {
          code,
        },
      )
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Failed to exchange token');
          }

          return response;
        }),
      );
  }

  getValidAccessToken() {
    const tokens = this.hubspotStorageService.getTokens();

    if (!tokens) {
      return throwError(() => new Error('No tokens available'));
    }

    if (this.isTokenExpired(tokens)) {
      return this.refreshAccessToken(tokens.refresh_token).pipe(
        map((newTokens) => newTokens.access_token),
      );
    }

    return of(tokens.access_token);
  }

  private refreshAccessToken(refreshToken: string) {
    if (this.refreshTokenInFlight$) {
      return this.refreshTokenInFlight$;
    }

    this.refreshTokenInFlight$ = this.http
      .post<HubspotTokens>(`${environment.endpoint}/v1/hubspot/refresh_token`, {
        refresh_token: refreshToken,
      })
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Failed to refresh token');
          }

          return response;
        }),
        tap((newTokens) => {
          this.hubspotStorageService.setTokens(newTokens);
          this.tokenData.set(newTokens);
        }),
        tap({
          complete: () => (this.refreshTokenInFlight$ = null),
          error: () => (this.refreshTokenInFlight$ = null),
        }),
        shareReplay(1),
      );

    return this.refreshTokenInFlight$;
  }

  private isTokenExpired(tokens: HubspotTokens): boolean {
    if (!tokens.exp) {
      return true;
    }

    return Date.now() >= tokens.exp * 1000;
  }

  getBearerToken() {
    const tokens = this.hubspotStorageService.getTokens();
    return tokens?.bearer;
  }

  logout() {
    this.hubspotStorageService.clear();
    this._isAuthenticated.set(false);
  }
}
